import React from 'react'
import { Link } from '@inertiajs/react'
import { CheckCircle2, CloudOff, Hourglass, TrendingDown } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { cn } from '@/lib/utils'
import { formatCount } from '@/lib/format'
import { formatPoints, paceBand } from '@/lib/plain-language'

/**
 * Band 5, card A — soft dates. The nights in the next 60 that are furthest BEHIND this
 * property's own booking curve, worst first. An action list: every row is a night the
 * operator could still move.
 *
 * ORDERING IS THE POINT, so the card leads with the quantity it orders by. `roomsBehind`
 * is how many room-nights the curve says would already be sold and are not — a 20-point
 * gap on a 40-room night is ten times the recoverable inventory of the same gap on a
 * 4-room night, and the bar in the last column makes that ranking legible without
 * reading a single number.
 *
 * ZERO MATH HERE. Every figure — occupancy, the baseline, the shortfall, the ratio, the
 * rooms — arrives computed from `SoftDatesMutation` and is printed verbatim (CLAUDE.md,
 * "Data Integrity"). The only derived value on this page is the bar's WIDTH, which is a
 * layout proportion and is never shown as a number.
 */

export interface SoftDate {
  date: string
  leadTimeDays: number
  observedOn: string
  segment: string
  occupancyPct: number
  baselineOccPct: number
  roomsSold: number
  roomsExpected: number
  roomsBehind: number
  capacity: number
  shortfallPts: number
  paceRatio: number
}

export interface SoftDatesData {
  dates: SoftDate[]
  softDateCount: number
  totalRoomsBehind: number
  horizonDays: number
  datesInHorizon: number
  datesAssessed: number
  /** null when there is a list; otherwise which of the three empty states applies. */
  reason: 'none_soft' | 'no_trusted_baseline' | 'no_occupancy_data' | null
  minShortfallPts: number
  minRoomsBehind: number
}

function parseDate(iso: string): Date | null {
  const parsed = new Date(`${iso}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function formatNight(iso: string): string {
  const parsed = parseDate(iso)
  if (!parsed) return iso
  return parsed.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

/** "tomorrow" / "in 4 nights" — the lead time in the words an operator uses. */
function leadPhrase(days: number): string {
  if (days <= 1) return 'tomorrow'
  return `in ${days} nights`
}

function segmentLabel(segment: string): string {
  if (segment === 'weekend') return 'weekend'
  if (segment === 'weekday') return 'weekday'
  return 'all days'
}

/**
 * How far behind, in three named tiers. Every row on this card is already soft, so there
 * is no "minor" tier — the mildest thing here still cleared both thresholds. Colours are
 * the design system's impact-level badges (moderate amber, major orange, extreme rose).
 */
const TIERS = [
  { max: 0.4, label: 'extreme', className: 'bg-rose-100 text-rose-700' },
  { max: 0.7, label: 'major', className: 'bg-orange-100 text-orange-700' },
  { max: Infinity, label: 'moderate', className: 'bg-amber-100 text-amber-700' },
] as const

function tierFor(paceRatio: number) {
  return TIERS.find((tier) => paceRatio < tier.max) ?? TIERS[TIERS.length - 1]
}

/** Month-scoped deep link — the prices calendar opens on the month holding that night. */
function calendarHref(propertyBasePath: string, iso: string): string {
  const parsed = parseDate(iso)
  if (!parsed) return `${propertyBasePath}/calendar/prices`
  return `${propertyBasePath}/calendar/prices?month=${parsed.getMonth() + 1}&year=${parsed.getFullYear()}`
}

const numericHead = 'h-10 px-4 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

interface EmptyStateProps {
  icon: React.ElementType
  title: string
  body: React.ReactNode
}

function EmptyState({ icon: Icon, title, body }: EmptyStateProps) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
      <Icon className="mx-auto h-8 w-8 text-muted-foreground/40" />
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  )
}

/**
 * The three empty states are three DIFFERENT claims and must never be collapsed into one
 * "no data" shrug: "nothing is soft" says the property is pacing fine, "not enough history"
 * says we decline to judge, and "no snapshots" says we have not looked yet. Telling an
 * operator they are pacing fine when we simply cannot see would be a lie.
 */
function SoftDatesEmpty({ soft }: { soft: SoftDatesData }) {
  if (soft.reason === 'none_soft') {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="Nothing is soft — you're pacing fine"
        body={
          <>
            All {formatCount(soft.datesAssessed)} of the next {soft.horizonDays} nights we could
            judge are at or above the occupancy this property normally holds that far out. Nothing
            here needs a decision today.
          </>
        }
      />
    )
  }

  if (soft.reason === 'no_trusted_baseline') {
    return (
      <EmptyState
        icon={Hourglass}
        title="Not enough booking history to judge these nights yet"
        body={
          <>
            A soft date is measured against this property's <em>own</em> booking curve, and there
            is not yet enough of one at these lead times. We will not borrow an industry
            average to make the call — that would send you to discount nights that were never
            soft. The curve fills in as bookings accumulate.
          </>
        }
      />
    )
  }

  return (
    <EmptyState
      icon={CloudOff}
      title="No booking snapshots for these nights yet"
      body={
        <>
          We record where every forward night stands each time we read your PMS. Once the
          first read lands, the next {soft.horizonDays} nights can be scored against your booking
          curve.
        </>
      }
    />
  )
}

export default function SoftDatesCard({
  soft,
  propertyBasePath,
}: {
  soft: SoftDatesData
  /** The property's backend-served canonical path. */
  propertyBasePath: string
}) {
  const rows = soft.dates
  const worst = rows.length > 0 ? rows[0].roomsBehind : 0
  const hidden = soft.softDateCount - rows.length

  return (
    <DashboardCard
      title="Soft dates"
      description={`Nights running behind this property's own booking curve, next ${soft.horizonDays} days`}
      icon={<TrendingDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          "Each night is compared with how full this property usually is that far ahead of the stay date, for that day type. Nights are ranked by room-nights behind — the rooms your own curve says would already be sold — so the biggest recoverable gap comes first, not the biggest percentage.",
        learnMore: '/guides/occupancy-metrics',
      }}
      action={
        rows.length > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-100 tabular-nums">
            {formatCount(soft.totalRoomsBehind)} room-nights behind
          </span>
        ) : undefined
      }
      padded={rows.length === 0}
      bodyClassName={rows.length === 0 ? 'p-5' : undefined}
      footer={
        rows.length > 0 ? (
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Ranked by <span className="font-semibold text-foreground">room-nights behind</span> —
            how many rooms this property's own curve says would be sold by now. A 20-point gap on
            a 40-room night outranks the same gap on a 4-room night, because only one of them has
            inventory worth recovering.
            {hidden > 0 && (
              <>
                {' '}
                Showing the worst {formatCount(rows.length)} of{' '}
                {formatCount(soft.softDateCount)} soft nights.
              </>
            )}
          </p>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <SoftDatesEmpty soft={soft} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-10 px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Night
              </TableHead>
              <TableHead className={numericHead}>Occupancy</TableHead>
              <TableHead className={cn(numericHead, 'whitespace-nowrap')}>Usual by now</TableHead>
              <TableHead className={numericHead}>Pace</TableHead>
              <TableHead className={cn(numericHead, 'whitespace-nowrap')}>Rooms behind</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const tier = tierFor(row.paceRatio)
              return (
                <TableRow key={row.date}>
                  <TableCell className="px-4 py-3">
                    <Link
                      href={calendarHref(propertyBasePath, row.date)}
                      className="text-sm font-medium text-foreground transition-colors hover:text-primary"
                    >
                      {formatNight(row.date)}
                    </Link>
                    <p className="whitespace-nowrap text-xs text-muted-foreground">
                      {leadPhrase(row.leadTimeDays)}
                      <span className="mx-1 text-slate-300">·</span>
                      {segmentLabel(row.segment)}
                      {/* Narrow screens scroll the table sideways, which would put the
                          ranking metric — the column the list is ordered by — off the
                          edge. Repeat it here so it is never the hidden one. */}
                      <span className="sm:hidden">
                        <span className="mx-1 text-slate-300">·</span>
                        <span className="font-semibold text-rose-600 tabular-nums">
                          {formatCount(row.roomsBehind)} behind
                        </span>
                      </span>
                    </p>
                  </TableCell>

                  <TableCell className="px-4 py-3 text-right">
                    <p className="text-sm font-semibold text-foreground tabular-nums">
                      {row.occupancyPct}%
                    </p>
                    <p className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                      {formatCount(row.roomsSold)} of {formatCount(row.capacity)} sold
                    </p>
                  </TableCell>

                  <TableCell className="px-4 py-3 text-right">
                    <p className="text-sm font-medium text-muted-foreground tabular-nums">
                      {row.baselineOccPct}%
                    </p>
                    <p className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                      about {formatCount(row.roomsExpected)} rooms
                    </p>
                  </TableCell>

                  <TableCell className="px-4 py-3 text-right">
                    {/* Pace is worded, never a bare multiplier — the colour still escalates
                        with the tier so severity reads at a glance (revenue-vocabulary §1). */}
                    <Badge
                      className={cn('whitespace-nowrap border-transparent text-[11px]', tier.className)}
                    >
                      {paceBand(row.paceRatio).label}
                    </Badge>
                    <p className="mt-0.5 whitespace-nowrap text-xs text-rose-600 tabular-nums">
                      {formatPoints(-row.shortfallPts)} occupancy
                    </p>
                  </TableCell>

                  <TableCell className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2.5">
                      {/* Magnitude only — the bar is scaled against the worst row so the
                          ranking reads at a glance. It is layout, never a printed figure. */}
                      <span
                        aria-hidden
                        className="hidden h-1.5 rounded-full bg-rose-400/70 sm:block"
                        style={{ width: `${worst > 0 ? Math.max(8, (row.roomsBehind / worst) * 56) : 0}px` }}
                      />
                      <span className="text-sm font-semibold text-rose-600 tabular-nums">
                        {formatCount(row.roomsBehind)}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </DashboardCard>
  )
}
